import PropTypes from 'prop-types';

import { Badge, Skeleton, Tooltip, Box } from '@mui/material';
import PeopleRoundedIcon from '@mui/icons-material/PeopleRounded';
import { useEffect, useMemo, useState } from 'react';

// project imports
import Avatar from 'components/@extended/Avatar';
import AvatarStatus from './AvatarStatus';
import { useGetImageByIdQuery } from 'store/features/api/apiSlice';

// assets
/* eslint-disable-next-line */
// const avatarImage = require.context('assets/images/icons', true);

const avatarImageModules = import.meta.glob('/src/assets/images/icons/*.{png,jpg,jpeg,svg}', {
	import: 'default',
});

const useAvatarImageSrc = (avatarName) => {
	const [avatarSrc, setAvatarSrc] = useState(undefined);

	const modulePath = useMemo(
		() => (avatarName ? `/src/assets/images/icons/${avatarName}` : ''),
		[avatarName]
	);

	useEffect(() => {
		let cancelled = false;

		const loadAvatar = async () => {
			if (!modulePath) {
				setAvatarSrc(undefined);
				return;
			}

			const loader = avatarImageModules[modulePath];
			if (!loader) {
				setAvatarSrc(undefined);
				return;
			}

			try {
				const src = await loader();
				if (!cancelled) setAvatarSrc(src);
			} catch {
				if (!cancelled) setAvatarSrc(undefined);
			}
		};

		loadAvatar();

		return () => {
			cancelled = true;
		};
	}, [modulePath]);

	return avatarSrc;
};

// ==============================|| CHAT USER AVATAR WITH STATUS ICON ||============================== //

const UserAvatar = ({ user, width = 30, height = 30, sx = {} }) => {
	let profile;
	const avatarSrc = useAvatarImageSrc(user?.avatar);

	try {
		profile = JSON.parse(user.base64)?.[0]?.thumbUrl;
	} catch (error) {
		profile = user.base64;
	}

	// console.log("user", user);

	// if (user?.base64 && user.avatar && user.avatar.length > 5) {
	//   profileData = JSON.parse(user.avatar)[0];
	// }

	return (
		<Badge
			overlap="circular"
			badgeContent={<AvatarStatus status={user?.online_status} />}
			anchorOrigin={{
				vertical: 'top',
				horizontal: 'left',
			}}
			sx={{
				'& .MuiBox-root': { width: 6, height: 6 },
				padding: 0,
				minWidth: 12,
				'& svg': { background: '#fff', borderRadius: '50%' },
				'& .MuiBadge-badge': { top: '2%', left: '3%' },
			}}
		>
			{profile ? (
				<Avatar variant="rounded" alt={user.name} src={profile} size="sm" sx={{ width, height, ...sx }} />
			) : !user?.name ? (
				<Avatar
					variant="rounded"
					alt={user.name}
					src={avatarSrc}
					size="sm"
					sx={{ width, height, ...sx }}
				/>
			) : (
				<Avatar
					alt="Avatar"
					variant="rounded"
					size="sm"
					sx={{
						color: 'primary.main',
						bgcolor: 'primary.lighter',
						width,
						height,
						...sx,
					}}
				>
					{user?.name ? user?.name?.[0]?.toUpperCase() : 'A'}
				</Avatar>
			)}
		</Badge>
	);
};

UserAvatar.propTypes = {
	user: PropTypes.object,
};

export default UserAvatar;

const UserAvatar2 = ({ imageObj, width = 30, height = 30, sx = {} }) => {
	let profileData = {};
	const avatarSrc = useAvatarImageSrc(imageObj?.avatar);

	if (imageObj?.b64PicProfile) {
		profileData = JSON.parse(imageObj.b64PicProfile)?.[0];
	}

	return (
		<Badge
			overlap="circular"
			badgeContent={<AvatarStatus status={imageObj?.online_status} />}
			anchorOrigin={{
				vertical: 'top',
				horizontal: 'left',
			}}
			sx={{
				'& .MuiBox-root': { width: 6, height: 6 },
				padding: 0,
				minWidth: 12,
				'& svg': { background: '#fff', borderRadius: '50%' },
				'& .MuiBadge-badge': { top: '2%', left: '3%' },
			}}
		>
			{imageObj?.b64PicProfile && profileData?.thumbUrl ? (
				<Avatar variant="rounded" alt={imageObj.entityFullName} src={profileData.thumbUrl} size="sm" sx={{ width, height, ...sx }} />
			) : imageObj?.avatar && imageObj?.avatar.length > 4 ? (
				<Avatar
					variant="rounded"
					alt={imageObj.name}
					src={avatarSrc}
					size="sm"
					sx={{ width, height, ...sx }}
				/>
			) : (
				// <Avatar
				//   alt="Avatar"
				//   variant="rounded"
				//   size="sm"
				//   sx={{
				//     color: "primary.main",
				//     bgcolor: "primary.lighter",
				//     width,
				//     height,
				//     ...sx,
				//   }}
				// >
				//   {imageObj?.name ? imageObj?.name?.[0]?.toUpperCase() : "A"}
				// </Avatar>
				<Skeleton animation="wave" variant="rounded" width={width} height={height} />
			)}
		</Badge>
	);
};

UserAvatar2.propTypes = {
	user: PropTypes.object,
};

const UserAvatar3 = ({
	variant = 'rounded',
	isToolTip = false,
	userId,
	onlineStatus,
	size = 'original',
	width = 30,
	height = 30,
	isGroup = false,
	setCascadeScore = () => {},
	sx = {},
}) => {
	// --- QUERY LOGIC ---
	const { data, isSuccess, isLoading, isError } = useGetImageByIdQuery({ userId, size }, { skip: userId ? false : true });

	useEffect(() => {
		if (data?.modelEntity) {
			const score = Math.ceil(data.modelEntity?.cascadeScore) ?? 0;
			setCascadeScore(score);
		}
	}, [data, setCascadeScore]);

	// --- RENDER CONTENT ---
	if (isSuccess && !isLoading) {
		if (data.modelEntity?.b64PicProfile) {
			let profile;
			try {
				profile = JSON.parse(data?.modelEntity?.b64PicProfile)?.[0]?.thumbUrl;
			} catch (error) {
				profile = data.modelEntity.b64PicProfile;
			}

			if (profile) {
				// 1. Define Configuration based on isGroup
				const badgeContent = isGroup ? (
					<Box
						sx={{
							bgcolor: '#000d1a', // Light grey background
							border: '2px solid #fff',
							borderRadius: '50%',
							width: 22,
							height: 22,
							display: 'flex',
							alignItems: 'center',
							justifyContent: 'center',
							color: '#666',
						}}
					>
						<PeopleRoundedIcon sx={{ color: 'white', fontSize: 14 }} />
					</Box>
				) : (
					<AvatarStatus status={onlineStatus} />
				);

				const anchorOrigin = isGroup ? { vertical: 'bottom', horizontal: 'right' } : { vertical: 'top', horizontal: 'left' };

				// 2. Define Styles based on mode
				const badgeSx = isGroup
					? {
							'& .MuiBadge-badge': {
								backgroundColor: 'transparent', // We handle bg in the Box above
								color: 'inherit',
								boxShadow: 'none',
								// Reset custom positioning for bottom-right
								top: 'auto',
								left: 'auto',
								right: '15%', // Fine tune position
								bottom: '12%',
								padding: 0,
							},
						}
					: {
							'& .MuiBox-root': { width: 6, height: 6 },
							padding: 0,
							minWidth: 12,
							'& svg': { background: '#fff', borderRadius: '50%', outline: '1px solid #ffff' },
							'& .MuiBadge-badge': { top: '2%', left: '3%' },
						};

				// 3. Construct the Core Avatar Element
				const AvatarComponent = (
					<Badge overlap="circular" badgeContent={badgeContent} anchorOrigin={anchorOrigin} sx={badgeSx}>
						<Avatar variant={variant} alt={data.modelEntity.entityFullName} src={profile} sx={{ width, height, ...sx }} loading="lazy" />
					</Badge>
				);

				// 4. Return with or without Tooltip
				return isToolTip ? <Tooltip title={data.modelEntity.entityFullName}>{AvatarComponent}</Tooltip> : AvatarComponent;
			}
		}
	}

	// --- LOADING STATE ---
	if (!isSuccess && !isError && isLoading) {
		return <Skeleton animation="wave" variant={variant} width={width} height={height} />;
	}

	// --- FALLBACK STATE ---
	return <Avatar variant={variant} alt="profile" sx={{ width, height, ...sx }} />;
};

UserAvatar3.propTypes = {
	user: PropTypes.object,
};

export { UserAvatar2, UserAvatar3 };
